/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import {
    array, string, shape, bool,
} from 'prop-types';
import { useUID } from 'react-uid';

import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import findRecentlyViewedProducts from '../../../../gql/queries/findRecentlyViewedProducts';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useGetAccessTokenSafely from '../../../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../../../gql';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getActiveABTests } from '../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import PersonalizedCarouselBuilder from '../Common/PersonalizedCarouselBuilder';
import { generateDataLayerProductArrays } from '../../../../helpers/tracking/vendors/Tealium/tealiumProductDataLayerHelpers';
import CarouselBuilderSkeleton from '../../GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import useVisibility from '../../../../helpers/hooks/useVisibility';
import useBrowserUUID from '../../../../helpers/useBrowserUUID';
import ComponentErrorBoundary from '../Common/ComponentErrorBoundary/ComponentErrorBoundary';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const removeCurrentProduct = ({ currentPartNumber, products }) => products.filter((product) => product.partNumber !== currentPartNumber);

const RecentlyViewedProductsCarouselBuilder = ({
    products, brand, recommendationData, isPdpRedesignEnabled, isPdpRedesignShortEnabled, isPlaRedesignEnabled, hideMoreRecommendation, dataTestId,
}) => {
    const dispatch = useDispatch();
    const optimizeABTests = useSelector(getActiveABTests);
    const isOptimizeScriptEnabled = useSelector(getFeatureFlag('is-optimize-script-enabled'));
    const isRecentlyViewedEnabled = useSelector(getFeatureFlag('is-pdp-recently-viewed-products-enabled'));
    const callbackId = useUID(); // used to generate unique callback id
    const [visible, ref] = useVisibility(callbackId);

    const productClickCallback = (partNumber) => {
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            eventSubType: 'feature-tracking',
            category: 'Recently Viewed',
            label: partNumber,
            page: { type: 'product' },
            partNumber,
        }));
    };

    const getRecentlyViewedProductsByType = (isVariant) => {
        // Callback returned with content (variant)
        if (isVariant) {
            const {
                title,
                title_font_color,
                title_font_size,
                title_font_style,
                title_font_family,
                title_font_weight,
                product_threshold,
                align_title,
                title_margin,
            } = recommendationData;
            const { color } = title_font_color || '';
            // 6810; add product_threshold in prs_v2 block or new service (v3)

            return (
                <ComponentErrorBoundary function="RecentlyViewedProductsCarouselBuilder" module="PersonalizedContent">
                    <PersonalizedCarouselBuilder
                        blockData={
                            {
                                title: title || 'Recently Viewed Products',
                                title_font_color: color,
                                title_font_size,
                                title_font_style,
                                title_font_family,
                                title_font_weight,
                                wcs_style: false,
                                align_title,
                                title_margin,
                            }
                        }
                        products={products?.length >= product_threshold ? products : []}
                        brand={brand}
                        eventTrackingData={{
                            recentlyViewed: true,
                        }}
                        isPdpSlider
                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                        isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
                        isPlaRedesignEnabled={isPlaRedesignEnabled}
                        hideMoreRecommendation={hideMoreRecommendation}
                        carouselUIComponent="recentlyViewedProductCarousel"
                        recentlyView
                        productClickCallback={productClickCallback}
                        dataTestId={dataTestId}
                    />
                </ComponentErrorBoundary>
            );
        }
        // Callback returned with no content (control)
        return null;
    };

    useEffect(() => {
        if (visible && products?.length >= recommendationData?.product_threshold) {
            if (isRecentlyViewedEnabled || optimizeABTests?.recentlyViewed) {
                const recentlyViewedProductsArray = products?.map((product) => ({
                    ...product,
                    list: 'Recently Viewed',
                }));
                const eventDataLayer = generateDataLayerProductArrays({
                    products: recentlyViewedProductsArray,
                    impressions: true,
                    props: {},
                });

                dispatch(trackEvent({
                    eventCategory: 'Recently Viewed',
                    eventAction: 'Impression',
                    eventLabel: 'Variant',
                    nonInteraction: true,
                    ...eventDataLayer,
                }));
            } else if (typeof optimizeABTests?.recentlyViewed === 'boolean') {
                dispatch(trackEvent({
                    eventCategory: 'Recently Viewed',
                    eventAction: 'Impression',
                    eventLabel: 'Control',
                    nonInteraction: true,
                }));
            }
        }
    }, [products, optimizeABTests?.recentlyViewed, visible]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (isOptimizeScriptEnabled) {
                window.dataLayer.push({ event: 'optimize.recentlyViewed' });
            }
        }
    }, []);

    return (
        <div data-staticid={callbackId} ref={ref}>
            {getRecentlyViewedProductsByType(products?.length > 0 && (isRecentlyViewedEnabled || optimizeABTests?.recentlyViewed))}
        </div>
    );
};

RecentlyViewedProductsCarouselBuilder.propTypes = {
    products: array.isRequired,
    brand: string.isRequired,
    recommendationData: shape({
        title_font_color: shape({
            color: string.isRequired,
        }),
        title_font_size: string,
        title_font_style: string,
        title_font_family: string,
        title_font_weight: string,
    }).isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
    hideMoreRecommendation: bool,
    dataTestId: string,
};
RecentlyViewedProductsCarouselBuilder.defaultProps = {
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
    hideMoreRecommendation: false,
    dataTestId: '',
};

const RecentlyViewedProductsCarouselContainer = ({
    partNumber, recommendationData, isPdpRedesignEnabled, isPdpRedesignShortEnabled, isPlaRedesignEnabled, hideMoreRecommendation, dataTestId,
}) => {
    const browserUUID = useBrowserUUID();
    const brand = useSelector(getBrandName);
    const jwt = useGetAccessTokenSafely();
    const isPersonalizationHoldoutEnabled = useSelector(getFeatureFlag('is-personalization-holdout-enabled'));

    const {
        loading, error, data,
    } = useQuery(findRecentlyViewedProducts(), {
        variables: {
            brand,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            user: {
                anonymousId: browserUUID,
            },
        },
        context: {
            api: 'personalizedContent',
            jwt,
        },
        skip: !jwt || !browserUUID,
        fetchPolicy: 'network-only',
    });

    if (!jwt || !browserUUID) {
        return null;
    }

    if (loading) {
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={{ mobile: 2, tablet: 3, desktop: 5 }}
                breakPoints={{ mobile: 601, desktop: 769 }}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: 'RecentlyViewedProductsCarouselContainer.js',
            message: 'Error loading data for recently viewed products',
            appName: process.env.npm_package_name,
            module: 'PDPRecentlyViewedProducts',
            error,
        });
        return null;
    }

    const products = removeCurrentProduct({ currentPartNumber: partNumber, products: data?.recentlyViewed?.products?.length > 0 ? data.recentlyViewed.products : [] });

    return <RecentlyViewedProductsCarouselBuilder products={products} brand={brand} recommendationData={recommendationData} isPdpRedesignEnabled={isPdpRedesignEnabled} isPdpRedesignShortEnabled={isPdpRedesignShortEnabled} isPlaRedesignEnabled={isPlaRedesignEnabled} hideMoreRecommendation={hideMoreRecommendation} dataTestId={dataTestId} />;
};

RecentlyViewedProductsCarouselContainer.propTypes = {
    partNumber: string.isRequired,
    recommendationData: shape({
        title_font_color: shape({
            color: string,
        }),
        title_font_size: string,
        title_font_style: string,
        title_font_family: string,
        title_font_weight: string,
    }).isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
    hideMoreRecommendation: bool,
    dataTestId: string,
};
RecentlyViewedProductsCarouselContainer.defaultProps = {
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
    hideMoreRecommendation: false,
    dataTestId: '',
};

export default RecentlyViewedProductsCarouselContainer;
